// deps
import React, { useMemo, memo, useCallback, useState } from "react";

// internal-deps
import {
    DeliverySpeedDetail,
    IStressComms,
    MenuItemsCalloutInfo,
    MROChildCart,
    Option,
    UserAddress,
    KNOW_MORE_SELF_DELIVERY,
} from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

// helpers
import { getMROChildCartRestaurantName, getSelectedSlaFromSlaOptions } from "@cart-ui/helpers/Mro";
import { getSlaText } from "@cart-ui/helpers/DeliverySpeed";

// components
import { ExpressIcon } from "../../delivery-options/icons";
import HalfCardWidget from "../../menu-items-callout/components/half-card-widget/HalfCardWidget";
import DeliveryHalfCard from "./DeliveryHalfCard";
import { Separator } from "./Separator";
import RestaurantImage from "./RestaurantImage";

// styles
import {
    CartHeaderWrapper,
    CommsText,
    ExpressIconWrapper,
    ImageWrapper,
    NameWrapper,
    RxNameText,
    RxNameWrapper,
    SeparatorWrapper,
    StyledText,
    TriggerTextContainer,
} from "../styles";
import { RX_NAME_IMAGE_SIZE, SELF_DELIVERY_METHOD_LABEL } from "@cart-ui/constants/MROCart";

interface MRORxCardHeaderProps {
    cart: MROChildCart;
    deliverySpeedWidgetInfoV2: {
        options: Option[];
        optInText?: string;
        slaComms?: IStressComms;
    };
    onDeliverySpeedItemClick: (item: DeliverySpeedDetail, fromHalfCard?: boolean, text?: string) => void;
    hideDeliverySpeedCrouton: VoidFunction;
    selectedSlaOption: DeliverySpeedDetail | undefined;
    deliverySpeedCroutonText?: string;
    deliverySpeedCroutonSubText?: string;
    deliverySpeedCroutonIcon?: string;
    onCroutonClick: VoidFunction;
    onCroutonLoad: VoidFunction;
    onOptInWidgetClick: (optInValue: boolean) => void;
    selectedAddress?: UserAddress;
    calloutInfo?: {
        isAvailable: boolean;
        info?: Partial<MenuItemsCalloutInfo>;
    };
    hasMroCartError?: boolean;
    isAddressSelected?: boolean;
}

const MRORxCardHeader = ({
    cart,
    deliverySpeedWidgetInfoV2,
    onDeliverySpeedItemClick,
    selectedSlaOption,
    onOptInWidgetClick,
    selectedAddress,
    calloutInfo,
    hasMroCartError,
    isAddressSelected,
}: MRORxCardHeaderProps) => {
    const selectedSlaFromSlaOptions = useMemo(() => {
        return getSelectedSlaFromSlaOptions(deliverySpeedWidgetInfoV2.options, selectedSlaOption);
    }, [deliverySpeedWidgetInfoV2.options, selectedSlaOption]);
    const rxName = getMROChildCartRestaurantName(cart);
    const [showMenuItemCalloutHalfCard, setShowMenuItemCalloutHalfCard] = useState(false);
    const [showSLAHalfCard, setShowSLAHalfCard] = useState(false);
    const [currentSla, setCurrentSla] = useState<Option | undefined>(selectedSlaFromSlaOptions);

    const isSelfDelivery = cart.isRestaurantSelfDeliveryV2Enabled === true;

    const { delivery_type_opt_in: deliveryTypeOptIn } = selectedAddress || {};

    const openHalfCard = useCallback(() => {
        if (isSelfDelivery || isEmpty(deliverySpeedWidgetInfoV2.options)) return;
        setCurrentSla(selectedSlaFromSlaOptions);
        setShowSLAHalfCard(true);
    }, [isSelfDelivery, deliverySpeedWidgetInfoV2.options, selectedSlaFromSlaOptions]);

    // P1: delivery_delay_message (OTP/bulk), P2: static self-delivery label
    // P0 (menu_items_callout) is rendered separately below with half-card support
    let subHeaderMessage = "";
    if (!calloutInfo?.isAvailable) {
        if (!isEmpty(deliverySpeedWidgetInfoV2.slaComms?.slaText)) {
            subHeaderMessage = deliverySpeedWidgetInfoV2.slaComms?.slaText ?? "";
        } else if (isSelfDelivery) {
            subHeaderMessage = SELF_DELIVERY_METHOD_LABEL;
        }
    }

    const bottomMessagePresent = useMemo(() => {
        return calloutInfo?.isAvailable || (!calloutInfo?.isAvailable && !isEmpty(subHeaderMessage));
    }, [subHeaderMessage, calloutInfo?.isAvailable]);

    return (
        <>
            <CartHeaderWrapper isAlignCenter={bottomMessagePresent}>
                <NameWrapper>
                    <RxNameWrapper>
                        <ImageWrapper
                            isMarginTop={bottomMessagePresent}
                            height={RX_NAME_IMAGE_SIZE.height}
                            width={RX_NAME_IMAGE_SIZE.width}
                        >
                            <RestaurantImage
                                cart={cart}
                                height={RX_NAME_IMAGE_SIZE.height}
                                width={RX_NAME_IMAGE_SIZE.width}
                                alt={rxName}
                            />
                        </ImageWrapper>
                        <div>
                            <RxNameText
                                variant="V2.body1.neutral.bold"
                                color="text_High_Emphasis"
                                maxLines={1}
                                bottomMessagePresent={bottomMessagePresent}
                            >
                                {rxName}
                            </RxNameText>
                            {calloutInfo?.isAvailable && (
                                <div>
                                    <CommsText variant="V2.overline.neutral.regular" color="text_Low_Emphasis">
                                        {calloutInfo?.info?.message}{" "}
                                        <TriggerTextContainer
                                            variant="V2.overline.neutral.regular"
                                            color="text_Low_Emphasis"
                                            onClick={() => setShowMenuItemCalloutHalfCard(true)}
                                            data-testid={KNOW_MORE_SELF_DELIVERY}
                                        >
                                            {calloutInfo?.info?.half_card_trigger_text}
                                        </TriggerTextContainer>
                                    </CommsText>

                                    <HalfCardWidget
                                        showHalfCard={showMenuItemCalloutHalfCard}
                                        halfCardInfo={calloutInfo?.info?.half_card_info}
                                        onClose={() => setShowMenuItemCalloutHalfCard(false)}
                                    />
                                </div>
                            )}
                            {!calloutInfo?.isAvailable && !isEmpty(subHeaderMessage) && (
                                <Text variant="V2.overline.neutral.regular" color="text_Low_Emphasis">
                                    {subHeaderMessage}
                                </Text>
                            )}
                        </div>
                    </RxNameWrapper>
                </NameWrapper>
                {hasMroCartError ? (
                    <Text variant="V2.overline.neutral.medium" color="IG_Primary">
                        {cart?.statusMessage}
                    </Text>
                ) : (
                    !isEmpty(isAddressSelected) && (
                        <StyledText
                            variant="V2.body3.neutral.medium"
                            textColor={selectedSlaFromSlaOptions?.meta?.highlightColor as keyof IColors}
                            onClick={openHalfCard}
                        >
                            <ExpressIconWrapper>
                                <ExpressIcon
                                    type={selectedSlaOption?.type}
                                    highlightColor={selectedSlaFromSlaOptions?.meta?.highlightColor}
                                    selected={selectedSlaFromSlaOptions?.option === selectedSlaOption?.type}
                                    disabled={selectedSlaFromSlaOptions?.isDisabled}
                                />
                            </ExpressIconWrapper>
                            {getSlaText(selectedSlaOption)}
                        </StyledText>
                    )
                )}
            </CartHeaderWrapper>
            <SeparatorWrapper>
                <Separator />
            </SeparatorWrapper>
            <DeliveryHalfCard
                cart={cart}
                deliverySpeedWidgetInfoV2={deliverySpeedWidgetInfoV2}
                onDeliverySpeedItemClick={onDeliverySpeedItemClick}
                selectedSlaOption={selectedSlaOption}
                onOptInWidgetClick={onOptInWidgetClick}
                deliveryTypeOptIn={deliveryTypeOptIn}
                currentSla={currentSla}
                isHalfCardOpen={showSLAHalfCard}
                setCurrentSla={setCurrentSla}
                setIsHalfCardOpen={setShowSLAHalfCard}
            />
        </>
    );
};

export default memo(MRORxCardHeader);
